import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, CreditCard, Send, Check, Sparkles, Heart, ArrowRight, Copy, CheckCircle, History, Clock, XCircle } from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface GiftCardOption {
  value: number;
  credits: number;
  popular?: boolean;
}

interface GiftCardHistory {
  id: number;
  code: string;
  amount: number;
  credits: number;
  recipient_name?: string;
  recipient_email?: string;
  sender_name?: string;
  status: string;
  created_at: string;
  redeemed_at?: string;
}

interface User {
  id: number;
  email: string;
  name: string;
  organization_id: number;
  role: string;
  subscription_tier: string;
  credits_remaining: number;
}

export default function GiftCards() {
  const { theme } = useThemeStore();
  const { token, updateUser } = useAuthStore();
  const isDark = theme === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [giftCode, setGiftCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Redeem state
  const [redeemCode, setRedeemCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);

  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [purchasedCards, setPurchasedCards] = useState<GiftCardHistory[]>([]);
  const [redeemedCards, setRedeemedCards] = useState<GiftCardHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const giftCardOptions: GiftCardOption[] = [
    { value: 25, credits: 500 },
    { value: 50, credits: 1000, popular: true },
    { value: 100, credits: 2500 },
    { value: 200, credits: 5500 },
  ];

  // Handle payment success/cancel from URL params
  useEffect(() => {
    const success = searchParams.get('success');
    const code = searchParams.get('code');
    const canceled = searchParams.get('canceled');
    const paypalSuccess = searchParams.get('paypal_success');
    const paypalCanceled = searchParams.get('paypal_canceled');

    if (success === 'true' && code) {
      setGiftCode(code);
      setSuccessMessage('Gift card purchased successfully! Share the code with the recipient.');
      // Clean URL
      setSearchParams({});
    } else if (canceled === 'true') {
      setErrorMessage('Payment was cancelled. Please try again.');
      setSearchParams({});
    } else if (paypalSuccess === 'true' && code) {
      // Capture PayPal payment
      capturePayPalPayment(code);
    } else if (paypalCanceled === 'true') {
      setErrorMessage('PayPal payment was cancelled. Please try again.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Capture PayPal payment
  const capturePayPalPayment = async (code: string) => {
    try {
      const orderId = searchParams.get('token'); // PayPal passes order ID as 'token'
      if (!orderId) {
        setErrorMessage('Missing PayPal order information');
        setSearchParams({});
        return;
      }

      const response = await fetch(`${API_URL}/giftcards/paypal/capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId, code }),
      });

      const data = await response.json() as { success: boolean; data?: { code: string }; error?: string };

      if (data.success && data.data?.code) {
        setGiftCode(data.data.code);
        setSuccessMessage('Gift card purchased successfully! Share the code with the recipient.');
      } else {
        setErrorMessage(data.error || 'Failed to complete PayPal payment');
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      setErrorMessage('Failed to complete PayPal payment');
    } finally {
      setSearchParams({});
    }
  };

  // Load gift card history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/giftcards/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json() as {
        success: boolean;
        data?: { purchased: GiftCardHistory[]; redeemed: GiftCardHistory[] };
        error?: string;
      };

      if (data.success && data.data) {
        setPurchasedCards(data.data.purchased || []);
        setRedeemedCards(data.data.redeemed || []);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (showHistory) {
      loadHistory();
    }
  }, [showHistory]);

  const getFinalAmount = (): number => {
    if (selectedAmount) return selectedAmount;
    const custom = parseFloat(customAmount);
    return isNaN(custom) ? 0 : custom;
  };

  const getCreditsForAmount = (amount: number): number => {
    // Base rate: $25 = 500 credits
    // Bonus credits for larger amounts
    if (amount >= 200) return Math.round(amount * 27.5); // ~10% bonus
    if (amount >= 100) return Math.round(amount * 25);   // ~5% bonus
    if (amount >= 50) return Math.round(amount * 20);
    return Math.round(amount * 20);
  };

  const handlePurchase = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = getFinalAmount();
    if (amount < 10) {
      setErrorMessage('Minimum gift card amount is $10');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`${API_URL}/giftcards/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount,
          credits: getCreditsForAmount(amount),
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          sender_name: senderName,
          message,
          delivery_date: deliveryDate || null,
        }),
      });

      const data = await response.json() as { success: boolean; data?: { checkout_url?: string; gift_code?: string; order_id?: string }; error?: string };

      if (data.success && data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else if (data.success && data.data?.gift_code) {
        setGiftCode(data.data.gift_code);
        setSuccessMessage('Gift card created successfully!');
      } else {
        setErrorMessage(data.error || 'Failed to create gift card');
      }
    } catch (error) {
      console.error('Failed to purchase gift card:', error);
      setErrorMessage('Failed to purchase gift card. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!redeemCode.trim()) {
      setErrorMessage('Please enter a gift card code');
      return;
    }

    setIsRedeeming(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`${API_URL}/giftcards/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: redeemCode }),
      });

      const data = await response.json() as { success: boolean; data?: { credits: number; user?: User }; error?: string };

      if (data.success && data.data) {
        setSuccessMessage(`Gift card redeemed! ${data.data.credits.toLocaleString()} credits have been added to your account.`);
        setRedeemCode('');

        // Update user credits in store
        if (data.data.user) {
          updateUser(data.data.user);
        }

        // Refresh history if visible
        if (showHistory) {
          loadHistory();
        }
      } else {
        setErrorMessage(data.error || 'Invalid or expired gift card code');
      }
    } catch (error) {
      console.error('Failed to redeem gift card:', error);
      setErrorMessage('Failed to redeem gift card. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };

  const copyGiftCode = () => {
    if (giftCode) {
      navigator.clipboard.writeText(giftCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 rounded-full text-xs bg-accent-green/20 text-accent-green">Active</span>;
      case 'redeemed':
        return <span className="px-2 py-1 rounded-full text-xs bg-accent-purple/20 text-accent-purple">Redeemed</span>;
      case 'pending':
        return <span className="px-2 py-1 rounded-full text-xs bg-accent-orange/20 text-accent-orange">Pending</span>;
      case 'expired':
        return <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">Expired</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">{status}</span>;
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="max-w-6xl mx-auto space-y-8 p-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-pink to-accent-orange mb-4">
          <Gift className="w-8 h-8 text-white" />
        </div>
        <h1 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Gift Cards
        </h1>
        <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
          Give the gift of AI-powered productivity. Perfect for colleagues, friends, or anyone who could use a digital assistant team.
        </p>

        {/* Toggle History Button */}
        <button
          onClick={() => setShowHistory(!showHistory)}
          className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            showHistory
              ? 'bg-accent-purple text-white'
              : isDark
              ? 'bg-dark-700 text-text-secondary hover:bg-dark-600'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <History className="w-4 h-4" />
          {showHistory ? 'Hide History' : 'View History'}
        </button>
      </motion.div>

      {/* Success/Error Messages */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-accent-green/20 border border-accent-green/30 text-accent-green px-4 py-3 rounded-xl text-center"
        >
          {successMessage}
        </motion.div>
      )}
      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-center"
        >
          {errorMessage}
        </motion.div>
      )}

      {/* Gift Code Display */}
      {giftCode && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`rounded-2xl p-6 border-2 border-accent-green text-center ${isDark ? 'bg-dark-800' : 'bg-white shadow-lg'}`}
        >
          <CheckCircle className="w-12 h-12 text-accent-green mx-auto mb-4" />
          <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Your Gift Card Code
          </h3>
          <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-mono text-xl ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
            <span className={isDark ? 'text-white' : 'text-slate-900'}>{giftCode}</span>
            <button onClick={copyGiftCode} className="text-accent-purple hover:text-accent-purple/80">
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <p className={`text-sm mt-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            Share this code with the recipient to redeem their credits
          </p>
        </motion.div>
      )}

      {/* History Section */}
      {showHistory && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {loadingHistory ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-accent-purple border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className={`mt-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Loading history...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Purchased Cards */}
              <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Send className="w-5 h-5 text-accent-pink" />
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Purchased Cards</h3>
                </div>
                {purchasedCards.length === 0 ? (
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>No gift cards purchased yet.</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {purchasedCards.map((card) => (
                      <div key={card.id} className={`p-3 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{card.code}</p>
                            <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                              To: {card.recipient_name}
                            </p>
                          </div>
                          {getStatusBadge(card.status)}
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>${card.amount}</span>
                          <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>{card.credits.toLocaleString()} credits</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Redeemed Cards */}
              <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-accent-green" />
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Redeemed Cards</h3>
                </div>
                {redeemedCards.length === 0 ? (
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>No gift cards redeemed yet.</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {redeemedCards.map((card) => (
                      <div key={card.id} className={`p-3 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{card.code}</p>
                            <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                              From: {card.sender_name}
                            </p>
                          </div>
                          <span className="px-2 py-1 rounded-full text-xs bg-accent-green/20 text-accent-green">
                            +{card.credits.toLocaleString()}
                          </span>
                        </div>
                        <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(card.redeemed_at || card.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Purchase Gift Card */}
        <motion.div variants={fadeInUp}>
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-accent-pink/10 flex items-center justify-center">
                <Send className="w-5 h-5 text-accent-pink" />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Send a Gift Card
                </h2>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Choose an amount and personalize your gift
                </p>
              </div>
            </div>

            <form onSubmit={handlePurchase} className="space-y-5">
              {/* Amount Selection */}
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                  Select Amount
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {giftCardOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSelectedAmount(option.value);
                        setCustomAmount('');
                      }}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        selectedAmount === option.value
                          ? 'border-accent-purple bg-accent-purple/10'
                          : isDark
                          ? 'border-border hover:border-white/20'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {option.popular && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs bg-accent-purple text-white px-2 py-0.5 rounded-full">
                          Popular
                        </span>
                      )}
                      <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        ${option.value}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        {option.credits.toLocaleString()} credits
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                  Or enter custom amount
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-lg ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                    $
                  </span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    placeholder="50"
                    min="10"
                    max="1000"
                    className={`w-full pl-8 pr-4 py-3 rounded-xl border ${
                      isDark
                        ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                        : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                    } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
                  />
                </div>
                {customAmount && (
                  <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    = {getCreditsForAmount(parseFloat(customAmount) || 0).toLocaleString()} credits
                  </p>
                )}
              </div>

              {/* Recipient Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Recipient Name
                  </label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className={`w-full px-4 py-3 rounded-xl border ${
                      isDark
                        ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                        : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                    } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Recipient Email
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="john@example.com"
                    required
                    className={`w-full px-4 py-3 rounded-xl border ${
                      isDark
                        ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                        : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                    } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
                  />
                </div>
              </div>

              {/* Sender Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                  Your Name (Sender)
                </label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Your name"
                  required
                  className={`w-full px-4 py-3 rounded-xl border ${
                    isDark
                      ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                      : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                  } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
                />
              </div>

              {/* Personal Message */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                  Personal Message (Optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal note..."
                  rows={3}
                  maxLength={500}
                  className={`w-full px-4 py-3 rounded-xl border resize-none ${
                    isDark
                      ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                      : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                  } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
                />
              </div>

              {/* Delivery Date */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                  Schedule Delivery (Optional)
                </label>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-3 rounded-xl border ${
                    isDark
                      ? 'bg-dark-700 border-border text-white'
                      : 'bg-white border-slate-200 text-slate-900'
                  } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Leave empty to send immediately
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || getFinalAmount() < 10}
                className="w-full btn-primary flex items-center justify-center gap-2 py-3"
              >
                {isSubmitting ? 'Processing...' : `Purchase Gift Card - $${getFinalAmount() || 0}`}
                <ArrowRight className="w-4 h-4" />
              </button>

              {/* Info note about where to find the code */}
              <div className={`flex items-start gap-3 p-4 rounded-xl ${isDark ? 'bg-accent-purple/10 border border-accent-purple/20' : 'bg-purple-50 border border-purple-100'}`}>
                <Gift className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-accent-purple' : 'text-purple-600'}`} />
                <div className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  <p className="font-medium mb-1">Where to find your gift card code:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>After payment, the code will appear on this page</li>
                    <li>Click <span className="font-medium">"View History"</span> above to see all your purchased codes</li>
                    <li>Share the code with the recipient to redeem credits</li>
                  </ul>
                </div>
              </div>
            </form>
          </div>
        </motion.div>

        {/* Redeem Gift Card */}
        <motion.div variants={fadeInUp} className="space-y-6">
          {/* Redeem Card */}
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-accent-green/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-accent-green" />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Redeem a Gift Card
                </h2>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Enter your gift card code to add credits
                </p>
              </div>
            </div>

            <form onSubmit={handleRedeem} className="space-y-4">
              <input
                type="text"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className={`w-full px-4 py-3 rounded-xl border font-mono text-center uppercase tracking-wider ${
                  isDark
                    ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                    : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
              />
              <button
                type="submit"
                disabled={isRedeeming || !redeemCode.trim()}
                className="w-full btn-secondary flex items-center justify-center gap-2"
              >
                {isRedeeming ? 'Redeeming...' : 'Redeem Gift Card'}
                <Check className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Benefits */}
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Why Gift SweetPilot?
            </h3>
            <div className="space-y-3">
              {[
                { icon: Sparkles, text: 'Instant delivery via email' },
                { icon: Heart, text: 'Perfect for any occasion' },
                { icon: Gift, text: 'No expiration date' },
                { icon: CreditCard, text: 'Flexible credit amounts' },
              ].map((item) => (
                <div key={item.text} className={`flex items-center gap-3 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  <item.icon className="w-5 h-5 text-accent-purple" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Corporate Bulk */}
          <div className={`rounded-2xl p-6 border bg-gradient-to-br ${isDark ? 'from-accent-purple/20 to-accent-pink/20 border-accent-purple/30' : 'from-purple-50 to-pink-50 border-purple-200'}`}>
            <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Corporate & Bulk Orders
            </h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-text-muted' : 'text-slate-600'}`}>
              Need gift cards for your team or clients? Get volume discounts on bulk purchases.
            </p>
            <a href="mailto:sales@sweetpilot.com" className="text-accent-purple hover:text-accent-purple/80 text-sm font-medium">
              Contact Sales →
            </a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
